import React, { useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { editUser } from "../../apis/UserAPI";

/**
 * EditCompanyUser Component - Edit user functionality for company dashboard
 *
 * Allows company admins to edit their team members' details including
 * name, email, and role within the company context
 *
 * @param {Object} props - Component props
 * @param {Object} props.user - The user object to edit
 * @param {Function} props.onUserEdited - Callback function called after successful edit
 * @param {Array} props.companyUsers - Array of all company users for validation
 * @param {number} props.companyId - The company ID (tid) for the user
 * @returns {JSX.Element} - The rendered edit user dialog component
 */
function EditCompanyUser({ user, onUserEdited, companyUsers, companyId }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    type_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Available user types for company context
  const companyUserTypes = [
    { type_id: 1, label: "Company User" },
    { type_id: 2, label: "Company Admin" },
  ];

  /**
   * Handle opening the edit dialog and populate form with user data
   */
  const handleClickOpen = () => {
    setFormData({
      name: user.name || "",
      contact: user.contact || "",
      type_id: user.type_id || 1,
    });
    setError("");
    setOpen(true);
  };

  /**
   * Handle closing the dialog and reset form
   */
  const handleClose = () => {
    setOpen(false);
    setFormData({
      name: "",
      contact: "",
      type_id: "",
    });
    setError("");
    setLoading(false);
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  /**
   * Validate form data before submission
   */
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return false;
    }

    if (!formData.contact.trim()) {
      setError("Email is required");
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.contact)) {
      setError("Please enter a valid email address");
      return false;
    }

    // Check if email is already taken by another user
    const emailExists = companyUsers.some(
      (u) => u.uid !== user.uid && u.contact === formData.contact
    );
    if (emailExists) {
      setError("This email is already in use by another team member");
      return false;
    }

    if (!formData.type_id) {
      setError("User role is required");
      return false;
    }

    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Prepare user data for API call
      const userData = {
        uid: user.uid,
        name: formData.name.trim(),
        contact: formData.contact.trim(),
        type_id: formData.type_id,
        tid: companyId, // Use the companyId prop instead of user.tid
        change_pass: "False", // Not changing password in this context
      };

      await editUser(userData);

      // Close dialog and notify parent component
      handleClose();
      if (onUserEdited) {
        onUserEdited();
      }
    } catch (error) {
      console.error("Error editing user:", error);
      setError(error.message || "Failed to update user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        startIcon={<Edit />}
        onClick={handleClickOpen}
        sx={{ minWidth: "auto" }}
      >
        Edit
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit,
        }}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Edit Team Member</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Update the details for {user.name}. Changes will be applied
            immediately.
          </DialogContentText>

          {error && (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              required
              fullWidth
              label='Full Name'
              value={formData.name}
              onChange={handleInputChange("name")}
              disabled={loading}
              variant='outlined'
            />

            <TextField
              required
              fullWidth
              label='Email Address'
              type='email'
              value={formData.contact}
              onChange={handleInputChange("contact")}
              disabled={loading}
              variant='outlined'
            />

            <FormControl
              fullWidth
              required
            >
              <InputLabel>User Role</InputLabel>
              <Select
                value={formData.type_id}
                label='User Role'
                onChange={handleInputChange("type_id")}
                disabled={loading}
              >
                {companyUserTypes.map((type) => (
                  <MenuItem
                    key={type.type_id}
                    value={type.type_id}
                  >
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant='outlined'
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? "Updating..." : "Update User"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EditCompanyUser;
